"use client";

import Image from "next/image";
import { Check, Loader, Copy, CheckCircle2 } from "lucide-react";
import { PricingItem, Pricing as PricingType } from "@/types/blocks/pricing";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useEffect, useRef, useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import Icon from "@/components/icon";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAppContext } from "@/contexts/app";
import { useLocale } from "next-intl";
import { trackEvent } from "@/lib/analytics";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";

const ZERO_DECIMAL_CURRENCIES = new Set([
  "bif",
  "clp",
  "djf",
  "gnf",
  "jpy",
  "kmf",
  "krw",
  "mga",
  "pyg",
  "rwf",
  "ugx",
  "vnd",
  "vuv",
  "xaf",
  "xof",
  "xpf",
]);

const minorToMajorCurrencyUnits = (amountMinor: number, currency: string) => {
  const normalizedCurrency = (currency || "").toLowerCase();
  if (ZERO_DECIMAL_CURRENCIES.has(normalizedCurrency)) {
    return Math.round(amountMinor);
  }

  return Math.round(amountMinor) / 100;
};

const formatPrice = (amountMinor: number, currency: string) => {
  const normalizedCurrency = (currency || "usd").toLowerCase();
  const upperCurrency = normalizedCurrency.toUpperCase();
  const zeroDecimal = ZERO_DECIMAL_CURRENCIES.has(normalizedCurrency);

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: upperCurrency,
    minimumFractionDigits: zeroDecimal ? 0 : 2,
    maximumFractionDigits: zeroDecimal ? 0 : 2,
  }).format(minorToMajorCurrencyUnits(amountMinor, normalizedCurrency));
};

const FEATURED_PLAN_KEY = "pro";

const PLAN_ORDER: Record<string, number> = {
  starter: 0,
  pro: 1,
  premium: 2,
};

const getPlanKey = (productId: string) =>
  productId.replace(/-(monthly|yearly)$/, "").trim().toLowerCase();

const getPerMonthAmountMinor = (item: PricingItem) => {
  if (item.interval === "year" && (item.valid_months || 0) > 0) {
    return Math.round(item.amount / (item.valid_months || 1));
  }

  return item.amount;
};

// Manual Payment Modal Component
function ManualPaymentModal({
  isOpen,
  onClose,
  item,
}: {
  isOpen: boolean;
  onClose: () => void;
  item: PricingItem | null;
}) {
  const [step, setStep] = useState<"qr" | "confirm" | "success">("qr");
  const [orderNo, setOrderNo] = useState("");
  const [amountYuan, setAmountYuan] = useState(0);
  const [transactionId, setTransactionId] = useState("");
  const [paymentMethod, setPaymentMethod] = useState<"alipay" | "wechat">("alipay");
  const [isLoading, setIsLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const locale = useLocale();

  // Get QR code URLs from environment
  const alipayQrUrl = process.env.NEXT_PUBLIC_ALIPAY_QR_URL || "";
  const wechatQrUrl = process.env.NEXT_PUBLIC_WECHAT_QR_URL || "";

  useEffect(() => {
    if (isOpen && item) {
      setStep("qr");
      setTransactionId("");
      setPaymentMethod("alipay");
      setCopied(false);
      createManualPaymentRequest();
    }
  }, [isOpen, item]);

  const createManualPaymentRequest = async () => {
    if (!item) return;
    
    setIsLoading(true);
    try {
      const response = await fetch("/api/manual-pay", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          product_id: item.product_id,
          locale: locale || "zh-cn",
        }),
      });

      const payload = await response.json();
      if (payload.code === 0 && payload.data) {
        setOrderNo(payload.data.order_no);
        setAmountYuan(payload.data.amount_yuan);
      } else {
        toast.error(payload.message || "创建支付请求失败");
        onClose();
      }
    } catch (e) {
      toast.error("网络错误，请重试");
      onClose();
    } finally {
      setIsLoading(false);
    }
  };

  const copyOrderNo = () => {
    navigator.clipboard.writeText(orderNo);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success("订单号已复制");
  };

  const submitConfirmation = async () => {
    if (!transactionId.trim()) {
      toast.error("请输入交易号");
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch("/api/manual-pay/confirm", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          order_no: orderNo,
          transaction_id: transactionId.trim(),
          payment_method: paymentMethod,
        }),
      });

      const payload = await response.json();
      if (payload.code === 0) {
        setStep("success");
        toast.success("支付信息已提交，请等待审核");
      } else {
        toast.error(payload.message || "提交失败");
      }
    } catch (e) {
      toast.error("网络错误，请重试");
    } finally {
      setIsLoading(false);
    }
  };

  const getCurrentQrUrl = () => {
    return paymentMethod === "alipay" ? alipayQrUrl : wechatQrUrl;
  };

  if (!item) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {step === "qr" && "扫码支付"}
            {step === "confirm" && "确认支付"}
            {step === "success" && "提交成功"}
          </DialogTitle>
          <DialogDescription>
            {step === "qr" && (
              <>
                {item.product_name || item.title} - ¥{amountYuan || item.cn_amount! / 100}
              </>
            )}
            {step === "confirm" && "请输入您的支付交易号以确认付款"}
            {step === "success" && "您的支付信息已提交，审核通过后将自动到账"}
          </DialogDescription>
        </DialogHeader>

        {step === "qr" && (
          <div className="space-y-4">
            {/* Order Info */}
            <div className="bg-muted p-3 rounded-lg">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">订单号</span>
                <div className="flex items-center gap-2">
                  <code className="text-sm font-mono">{orderNo || "加载中..."}</code>
                  {orderNo && (
                    <Button variant="ghost" size="icon" className="h-6 w-6" onClick={copyOrderNo}>
                      {copied ? <CheckCircle2 className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                    </Button>
                  )}
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                请备注此订单号在转账说明中
              </p>
            </div>

            {/* Payment Method Tabs */}
            <Tabs value={paymentMethod} onValueChange={(v) => setPaymentMethod(v as "alipay" | "wechat")}>
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="alipay">支付宝</TabsTrigger>
                <TabsTrigger value="wechat">微信支付</TabsTrigger>
              </TabsList>
              
              <TabsContent value="alipay" className="mt-4">
                <div className="flex flex-col items-center space-y-3">
                  {alipayQrUrl ? (
                    <div className="relative w-48 h-48 bg-white rounded-lg overflow-hidden">
                      <Image
                        src={alipayQrUrl}
                        alt="支付宝收款码"
                        fill
                        className="object-contain p-2"
                      />
                    </div>
                  ) : (
                    <div className="w-48 h-48 bg-muted rounded-lg flex items-center justify-center text-muted-foreground text-sm">
                      未配置收款码
                    </div>
                  )}
                  <p className="text-sm text-muted-foreground text-center">
                    请使用支付宝扫码支付 ¥{amountYuan || item.cn_amount! / 100}
                  </p>
                </div>
              </TabsContent>

              <TabsContent value="wechat" className="mt-4">
                <div className="flex flex-col items-center space-y-3">
                  {wechatQrUrl ? (
                    <div className="relative w-48 h-48 bg-white rounded-lg overflow-hidden">
                      <Image
                        src={wechatQrUrl}
                        alt="微信收款码"
                        fill
                        className="object-contain p-2"
                      />
                    </div>
                  ) : (
                    <div className="w-48 h-48 bg-muted rounded-lg flex items-center justify-center text-muted-foreground text-sm">
                      未配置收款码
                    </div>
                  )}
                  <p className="text-sm text-muted-foreground text-center">
                    请使用微信扫码支付 ¥{amountYuan || item.cn_amount! / 100}
                  </p>
                </div>
              </TabsContent>
            </Tabs>

            <div className="text-xs text-muted-foreground space-y-1">
              <p>1. 请扫码支付对应金额</p>
              <p>2. 务必备注订单号：{orderNo || "..."}</p>
              <p>3. 支付完成后点击"我已支付"</p>
            </div>

            <Button 
              className="w-full" 
              onClick={() => setStep("confirm")}
              disabled={isLoading || !orderNo}
            >
              我已支付
            </Button>
          </div>
        )}

        {step === "confirm" && (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="payment-method">支付方式</Label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant={paymentMethod === "alipay" ? "default" : "outline"}
                  onClick={() => setPaymentMethod("alipay")}
                  className="flex-1"
                >
                  支付宝
                </Button>
                <Button
                  type="button"
                  variant={paymentMethod === "wechat" ? "default" : "outline"}
                  onClick={() => setPaymentMethod("wechat")}
                  className="flex-1"
                >
                  微信支付
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="transaction-id">
                交易号 / 订单号
                <span className="text-xs text-muted-foreground ml-1">
                  (可在账单详情中查看)
                </span>
              </Label>
              <Input
                id="transaction-id"
                placeholder="请输入交易号..."
                value={transactionId}
                onChange={(e) => setTransactionId(e.target.value)}
              />
            </div>

            <div className="bg-muted p-3 rounded-lg text-sm">
              <p className="font-medium">支付金额：¥{amountYuan}</p>
              <p className="text-muted-foreground">商品：{item.product_name || item.title}</p>
            </div>

            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setStep("qr")} className="flex-1">
                返回
              </Button>
              <Button 
                onClick={submitConfirmation} 
                disabled={isLoading || !transactionId.trim()}
                className="flex-1"
              >
                {isLoading && <Loader className="mr-2 h-4 w-4 animate-spin" />}
                提交确认
              </Button>
            </div>
          </div>
        )}

        {step === "success" && (
          <div className="space-y-4 text-center py-4">
            <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto" />
            <div>
              <p className="font-medium">支付信息已提交</p>
              <p className="text-sm text-muted-foreground mt-1">
                订单号：{orderNo}
              </p>
              <p className="text-sm text-muted-foreground">
                审核通过后积分将自动到账
              </p>
            </div>
            <Button onClick={onClose} className="w-full">
              完成
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function Pricing({
  pricing,
  isStandalone = false,
}: {
  pricing: PricingType;
  isStandalone?: boolean;
}) {
  if (pricing.disabled) {
    return null;
  }

  const description = pricing.description
    ?.replace(/\s*Support:\s*support@(?:easyclaw|easyclawmart)\.pro\.?/i, "")
    .trim();

  const checkoutPaused =
    process.env.NEXT_PUBLIC_PAUSE_CHECKOUT === "true";

  const locale = useLocale();
  const { setShowSignModal } = useAppContext();
  const uiSource = isStandalone ? "pricing_page" : "pricing_section";

  const [group, setGroup] = useState(() => {
    return pricing.groups?.[0]?.name;
  });
  const [isLoading, setIsLoading] = useState(false);
  const [productId, setProductId] = useState<string | null>(null);
  const [manualPayItem, setManualPayItem] = useState<PricingItem | null>(null);
  const [isManualPayModalOpen, setIsManualPayModalOpen] = useState(false);
  
  const allItems = pricing.items ?? [];
  const visibleItems =
    allItems.filter((item) => !item.group || item.group === group) ?? [];
  const orderedVisibleItems = [...visibleItems].sort((left, right) => {
    return (PLAN_ORDER[getPlanKey(left.product_id)] ?? 99) -
      (PLAN_ORDER[getPlanKey(right.product_id)] ?? 99);
  });
  const monthlyItemByPlanKey = new Map(
    allItems
      .filter((item) => item.interval === "month")
      .map((item) => [getPlanKey(item.product_id), item] as const)
  );
  const featuredItem =
    orderedVisibleItems.find(
      (item) => getPlanKey(item.product_id) === FEATURED_PLAN_KEY
    ) ||
    orderedVisibleItems.find((item) => item.is_featured) ||
    orderedVisibleItems[0];
  const orderedVisibleItemIds = orderedVisibleItems
    .map((item) => item.product_id)
    .join(",");
  const summaryFromMinor = orderedVisibleItems.length
    ? Math.min(...orderedVisibleItems.map((item) => getPerMonthAmountMinor(item)))
    : null;
  const summaryFromLabel =
    summaryFromMinor !== null && orderedVisibleItems[0]
      ? formatPrice(summaryFromMinor, orderedVisibleItems[0].currency || "usd")
      : null;

  const isChineseLocale = locale?.startsWith("zh") || false;

  const isAuthError = (message?: string | null) => {
    if (!message) return false;
    const normalized = message.toLowerCase();
    return (
      normalized.includes("no auth") ||
      normalized.includes("unauth") ||
      normalized.includes("sign-in") ||
      normalized.includes("sign in")
    );
  };

  const handleCheckout = async (item: PricingItem, cn_pay: boolean = false) => {
    try {
      const eventCurrency = (cn_pay ? "cny" : item.currency) || "usd";
      const gaCurrency = eventCurrency.toUpperCase();
      const amountMinor =
        cn_pay && typeof item.cn_amount === "number" ? item.cn_amount : item.amount;
      const value = minorToMajorCurrencyUnits(amountMinor, eventCurrency);

      const gaItem = {
        item_id: item.product_id,
        item_name: item.product_name || item.title || item.product_id,
        price: value,
        currency: gaCurrency,
        quantity: 1,
      };

      const emitCheckoutError = ({
        httpStatus,
        errorCode,
        provider,
      }: {
        httpStatus?: number;
        errorCode?: string | null;
        provider?: string | null;
      }) => {
        trackEvent("checkout_error", {
          plan_id: item.product_id,
          http_status: typeof httpStatus === "number" ? httpStatus : undefined,
          error_code: errorCode || "unknown",
          provider: provider || "unknown",
          ui_source: uiSource,
        });
      };

      trackEvent("select_item", {
        item_list_id: "pricing",
        item_list_name: group || "",
        items: [gaItem],
      });

      trackEvent("view_item", {
        currency: gaCurrency,
        value,
        items: [gaItem],
      });

      trackEvent("begin_checkout", {
        plan_id: item.product_id,
        interval: item.interval,
        currency: gaCurrency,
        value,
        items: [gaItem],
        ui_source: uiSource,
      });

      const params = {
        product_id: item.product_id,
        currency: eventCurrency,
        locale: locale || "en",
      };

      setIsLoading(true);
      setProductId(item.product_id);

      const response = await fetch("/api/checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(params),
      });

      const payload = await response.json().catch(() => null);

      if (response.status === 401) {
        emitCheckoutError({ httpStatus: response.status, errorCode: "http_401" });
        setShowSignModal(true);
        return;
      }

      if (!payload) {
        emitCheckoutError({ httpStatus: response.status, errorCode: "invalid_json" });
        toast.error("checkout failed");
        return;
      }

      const { code, message, data } = payload;
      if (code !== 0) {
        emitCheckoutError({
          httpStatus: response.status,
          errorCode: typeof code === "number" ? String(code) : "unknown",
          provider: data?.provider,
        });
        if (isAuthError(message)) {
          setShowSignModal(true);
          return;
        }
        toast.error(message || "checkout failed");
        return;
      }

      const { checkout_url } = data;
      if (!checkout_url) {
        emitCheckoutError({
          httpStatus: response.status,
          errorCode: "missing_checkout_url",
          provider: data?.provider,
        });
        toast.error("checkout failed");
        return;
      }

      window.location.href = checkout_url;
    } catch (e) {
      console.log("checkout failed: ", e);
      trackEvent("checkout_error", {
        plan_id: item.product_id,
        error_code: "exception",
        ui_source: uiSource,
      });
      toast.error("checkout failed");
    } finally {
      setIsLoading(false);
      setProductId(null);
    }
  };

  // Handle manual payment for Chinese users
  const handleManualPay = async (item: PricingItem) => {
    trackEvent("begin_checkout", {
      plan_id: item.product_id,
      interval: item.interval,
      currency: "CNY",
      value: (item.cn_amount || 0) / 100,
      ui_source: uiSource,
      payment_method: "manual_qr",
    });

    setManualPayItem(item);
    setIsManualPayModalOpen(true);
  };

  const viewItemListKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!orderedVisibleItems.length) return;

    const key = `${group || ""}:${orderedVisibleItemIds}`;
    if (viewItemListKeyRef.current === key) return;

    viewItemListKeyRef.current = key;

    trackEvent("view_item_list", {
      item_list_id: "pricing",
      item_list_name: group || "",
      items: orderedVisibleItems.map((item) => ({
        item_id: item.product_id,
        item_name: item.product_name || item.title || item.product_id,
        price: minorToMajorCurrencyUnits(item.amount, item.currency || "usd"),
        currency: (item.currency || "usd").toUpperCase(),
        quantity: 1,
      })),
    });
  }, [group, orderedVisibleItemIds, orderedVisibleItems]);

  useEffect(() => {
    if (featuredItem?.product_id) {
      setProductId(featuredItem.product_id);
      setIsLoading(false);
    }
  }, [featuredItem?.product_id, orderedVisibleItemIds]);

  const TitleTag = isStandalone ? "h1" : "h2";
  const activeGroup = pricing.groups?.find((item) => item.name === group);

  return (
    <>
      <section
        id={pricing.name}
        className={cn(
          "relative overflow-hidden",
          isStandalone ? "pb-18 pt-30 md:pb-24 md:pt-38" : "landing-section py-16"
        )}
      >
        {isStandalone ? (
          <div className="pointer-events-none absolute inset-0">
            <div className="absolute left-1/2 top-[-220px] h-[540px] w-[540px] -translate-x-1/2 rounded-full bg-primary/20 blur-[140px]" />
            <div className="absolute left-[6%] top-[16%] h-[300px] w-[300px] rounded-full bg-[#ff8a7c]/12 blur-[120px]" />
            <div className="absolute right-[8%] top-[18%] h-[320px] w-[320px] rounded-full bg-cyan-400/8 blur-[120px]" />
          </div>
        ) : null}

        <div className="container">
          <div
            className={cn(
              "relative z-10",
              isStandalone ? "grid gap-8 lg:grid-cols-[1.15fr_0.85fr] lg:items-end" : ""
            )}
          >
            <div className={cn(isStandalone ? "max-w-4xl" : "mx-auto max-w-4xl text-center")}>
              <Badge
                variant="outline"
                className="border-white/14 bg-white/6 px-4 py-1.5 text-sm text-foreground/88"
              >
                Monthly Plans
              </Badge>
              <TitleTag
                className={cn(
                  "landing-title mb-4 mt-5 font-semibold tracking-tight",
                  isStandalone ? "text-5xl sm:text-6xl lg:text-7xl" : "text-4xl lg:text-5xl"
                )}
              >
                {pricing.title}
              </TitleTag>
              <p className="max-w-3xl text-base text-muted-foreground sm:text-lg lg:text-xl">
                {description}
              </p>

              <div className="mt-8 grid gap-3 sm:grid-cols-3">
                {orderedVisibleItems.map((item) => {
                  const planKey = getPlanKey(item.product_id);
                  const isVisualFeatured = planKey === FEATURED_PLAN_KEY;
                  const eyebrow = isVisualFeatured
                    ? "Main plan"
                    : planKey === "starter"
                    ? "Start lean"
                    : "Scale further";

                  return (
                    <div
                      key={`hero-${item.product_id}`}
                      className={cn(
                        "rounded-[26px] border px-4 py-4 text-left shadow-[0_18px_44px_rgba(0,0,0,0.22)] backdrop-blur-xl",
                        isVisualFeatured
                          ? "border-primary/40 bg-[linear-gradient(165deg,rgba(39,24,22,0.92),rgba(13,16,25,0.9))]"
                          : "border-white/10 bg-[linear-gradient(165deg,rgba(13,19,30,0.82),rgba(7,11,19,0.78))]"
                      )}
                    >
                      <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-foreground/55">
                        {eyebrow}
                      </p>
                      <div className="mt-3 flex items-end justify-between gap-3">
                        <p className="text-xl font-semibold text-foreground">{item.title}</p>
                        <p className="text-sm font-medium text-[#ffb099]">
                          {formatPrice(getPerMonthAmountMinor(item), item.currency || "usd")}
                          <span className="text-foreground/55">/mo</span>
                        </p>
                      </div>
                      <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                        {item.description}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>

            <div
              data-pricing-summary
              className="rounded-[34px] border border-white/12 bg-[linear-gradient(160deg,rgba(16,24,38,0.95),rgba(8,12,20,0.92))] p-6 shadow-[0_26px_80px_rgba(0,0,0,0.34)] backdrop-blur-xl sm:p-7"
            >
              <p className="text-xs font-semibold uppercase tracking-[0.28em] text-foreground/55">
                Billing
              </p>
              <p className="mt-3 text-2xl font-semibold text-foreground sm:text-3xl">
                Make the plan decision once.
              </p>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground sm:text-base">
                Pro is the default operating tier for teams that use OpenClaw
                weekly, with Starter and Premium framing the lighter and heavier ends.
              </p>

              {pricing.groups && pricing.groups.length > 0 && (
                <div className="mt-6 rounded-[26px] border border-white/10 bg-white/[0.04] p-1.5">
                  <RadioGroup
                    value={group}
                    className="grid gap-1"
                    style={{
                      gridTemplateColumns: `repeat(${pricing.groups.length}, minmax(0, 1fr))`,
                    }}
                    onValueChange={(value) => setGroup(value)}
                  >
                    {pricing.groups.map((item, i) => (
                      <div
                        key={i}
                        className='rounded-[22px] transition-all has-[button[data-state="checked"]]:bg-[linear-gradient(180deg,rgba(255,255,255,0.18),rgba(255,255,255,0.08))] has-[button[data-state="checked"]]:shadow-[0_10px_24px_rgba(0,0,0,0.18)]'
                      >
                        <RadioGroupItem
                          value={item.name || ""}
                          id={item.name}
                          className="peer sr-only"
                        />
                        <Label
                          htmlFor={item.name}
                          className="flex min-h-[64px] cursor-pointer flex-col items-center justify-center gap-1 rounded-[22px] px-4 py-3 text-center text-sm font-semibold text-muted-foreground peer-data-[state=checked]:text-foreground"
                        >
                          <span>{item.title}</span>
                          {item.label ? (
                            <span className="text-[11px] font-medium uppercase tracking-[0.2em] text-[#ff9f93]">
                              {item.label}
                            </span>
                          ) : null}
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                </div>
              )}

              <div className="mt-6 grid gap-3 sm:grid-cols-3 lg:grid-cols-1">
                <div
                  data-pricing-stat="from-price"
                  className="rounded-[24px] border border-white/10 bg-white/[0.03] px-4 py-4"
                >
                  <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-foreground/50">
                    From
                  </p>
                  <p className="mt-2 text-2xl font-semibold text-foreground">
                    {summaryFromLabel ? `${summaryFromLabel}/mo` : "--"}
                  </p>
                </div>
                <div
                  data-pricing-stat="plan-count"
                  className="rounded-[24px] border border-white/10 bg-white/[0.03] px-4 py-4"
                >
                  <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-foreground/50">
                    Plans
                  </p>
                  <p className="mt-2 text-2xl font-semibold text-foreground">
                    {orderedVisibleItems.length}
                  </p>
                </div>
                <div
                  data-pricing-stat="billing-flex"
                  className="rounded-[24px] border border-white/10 bg-white/[0.03] px-4 py-4"
                >
                  <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-foreground/50">
                    Billing flex
                  </p>
                  <p className="mt-2 text-2xl font-semibold text-foreground">
                    Monthly or annual
                  </p>
                </div>
              </div>

              {featuredItem ? (
                <div className="mt-6 rounded-[28px] border border-primary/24 bg-[radial-gradient(circle_at_top,rgba(255,131,96,0.18),transparent_65%),linear-gradient(180deg,rgba(30,18,16,0.88),rgba(13,16,25,0.92))] p-5">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#ff9f93]">
                    Current spotlight
                  </p>
                  <div className="mt-3 flex items-center justify-between gap-4">
                    <div>
                      <p className="text-xl font-semibold text-foreground">
                        {featuredItem.title}
                      </p>
                      <p className="mt-1 text-sm text-muted-foreground">
                        {featuredItem.description}
                      </p>
                    </div>
                    <Badge
                      variant="outline"
                      className="border-primary/30 bg-primary/12 px-3 text-primary-foreground"
                    >
                      Most Popular
                    </Badge>
                  </div>
                </div>
              ) : null}
            </div>
          </div>

          <div className="relative z-10 mt-10 grid gap-6 lg:mt-14 lg:grid-cols-3 lg:items-stretch">
            {orderedVisibleItems.map((item, index) => {
                const isItemLoading = isLoading && productId === item.product_id;
                const showManualPay = isChineseLocale && item.cn_amount && item.cn_amount > 0;
                const buttonTitle = item.button?.title || "Subscribe";
                const planKey = getPlanKey(item.product_id);
                const isVisualFeatured = planKey === FEATURED_PLAN_KEY;
                const highlightFeatures = item.features?.slice(0, 2) ?? [];
                const detailFeatures =
                  item.features && item.features.length > 2
                    ? item.features.slice(2)
                    : item.features ?? [];
                const isAnnualPlan = item.interval === "year" && (item.valid_months || 0) > 0;
                const monthlyEquivalentMinor = getPerMonthAmountMinor(item);
                const displayPrice = isAnnualPlan
                  ? formatPrice(monthlyEquivalentMinor, item.currency || "usd")
                  : item.price || formatPrice(item.amount, item.currency || "usd");
                const displayUnit = isAnnualPlan ? "/mo" : item.unit;
                const billingNote = isAnnualPlan
                  ? `Billed annually at ${formatPrice(item.amount, item.currency || "usd")}/year`
                  : null;
                const monthlyItem = monthlyItemByPlanKey.get(planKey);
                const annualSavingsMinor =
                  isAnnualPlan && monthlyItem
                    ? Math.max(monthlyItem.amount * 12 - item.amount, 0)
                    : 0;
                const annualSavingsLabel =
                  annualSavingsMinor > 0
                    ? `Save ${formatPrice(annualSavingsMinor, item.currency || "usd")}/year`
                    : null;
                const displayLabel = isVisualFeatured
                  ? "Most Popular"
                  : item.label === "Most Popular"
                  ? undefined
                  : item.label;
                const planEyebrow =
                  planKey === "starter"
                    ? "Lean start"
                    : isVisualFeatured
                    ? "Weekly operator default"
                    : "High-capacity path";

                return (
                  <article
                    key={`${item.product_id}-${index}`}
                    data-pricing-card={item.product_id}
                    data-pricing-card-featured={isVisualFeatured ? "true" : "false"}
                    className={cn(
                      "relative overflow-hidden rounded-[34px] p-7 shadow-[0_28px_70px_rgba(0,0,0,0.34)] backdrop-blur-xl transition-transform duration-300",
                      isVisualFeatured
                        ? "border border-primary/45 bg-[radial-gradient(circle_at_top,rgba(255,131,96,0.22),transparent_55%),linear-gradient(160deg,rgba(31,19,18,0.95),rgba(14,17,28,0.93))] text-card-foreground ring-1 ring-primary/30 lg:-translate-y-4"
                        : planKey === "premium"
                        ? "border border-amber-100/10 bg-[linear-gradient(165deg,rgba(18,21,30,0.95),rgba(8,11,18,0.9))]"
                        : "border border-white/10 bg-[linear-gradient(165deg,rgba(13,19,30,0.94),rgba(7,11,19,0.88))]"
                    )}
                  >
                    <div
                      className={cn(
                        "pointer-events-none absolute right-[-70px] top-[-60px] h-[180px] w-[180px] rounded-full blur-[70px]",
                        isVisualFeatured ? "bg-primary/22" : "bg-primary/10"
                      )}
                    />
                    <div className="flex h-full flex-col">
                      <div className="flex items-start gap-4">
                        <div className="min-w-0">
                          <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-foreground/55">
                            {planEyebrow}
                          </p>
                          {item.title ? (
                            <h3 className="mt-3 text-2xl font-semibold sm:text-[2rem]">
                              {item.title}
                            </h3>
                          ) : null}
                        </div>
                        <div className="ml-auto flex shrink-0">
                          {displayLabel ? (
                            <Badge
                              variant="outline"
                              className={cn(
                                "px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em]",
                                isVisualFeatured
                                  ? "border-primary/30 bg-primary text-primary-foreground"
                                  : "border-white/14 bg-white/6 text-foreground/86"
                              )}
                            >
                              {displayLabel}
                            </Badge>
                          ) : null}
                        </div>
                      </div>

                      {item.description ? (
                        <p className="mt-4 max-w-sm text-sm leading-relaxed text-muted-foreground sm:text-base">
                          {item.description}
                        </p>
                      ) : null}

                      <div className="mt-6 rounded-[28px] border border-white/10 bg-black/10 p-5">
                        <div className="flex flex-wrap items-end gap-3">
                          <div className="flex items-end gap-3">
                            {displayPrice && (
                              <span className="text-5xl font-semibold tracking-tight sm:text-6xl">
                                {displayPrice}
                              </span>
                            )}
                            {displayUnit && (
                              <span className="mb-1 block text-base font-semibold text-muted-foreground">
                                {displayUnit}
                              </span>
                            )}
                          </div>
                          {annualSavingsLabel ? (
                            <Badge
                              variant="outline"
                              className="mb-2 border-emerald-300/30 bg-emerald-300/12 text-emerald-100"
                            >
                              {annualSavingsLabel}
                            </Badge>
                          ) : null}
                        </div>
                        <div className="mt-3 space-y-3">
                          {billingNote ? (
                            <div className="text-sm font-medium text-muted-foreground/90">
                              {billingNote}
                            </div>
                          ) : null}
                          {item.original_price && (
                            <div className="mt-3 text-sm font-medium uppercase tracking-[0.18em] text-muted-foreground/90 line-through">
                              {item.original_price}
                            </div>
                          )}
                        </div>
                      </div>

                      {highlightFeatures.length > 0 ? (
                        <div
                          data-pricing-highlights
                          className="mt-6 flex flex-wrap gap-2"
                        >
                          {highlightFeatures.map((feature, fi) => (
                            <div
                              key={`highlight-${item.product_id}-${fi}`}
                              className={cn(
                                "rounded-full border px-3 py-1.5 text-xs font-medium",
                                isVisualFeatured
                                  ? "border-primary/25 bg-primary/10 text-foreground"
                                  : "border-white/12 bg-white/[0.05] text-foreground/88"
                              )}
                            >
                              {feature}
                            </div>
                          ))}
                        </div>
                      ) : null}

                      {item.features_title ? (
                        <p className="mb-4 mt-7 text-sm font-semibold uppercase tracking-[0.18em] text-foreground/70">
                          {item.features_title}
                        </p>
                      ) : null}

                      {detailFeatures.length > 0 ? (
                        <ul className="flex flex-col gap-3.5">
                          {detailFeatures.map((feature, fi) => (
                            <li
                              className="flex gap-3 text-sm leading-relaxed text-foreground/90"
                              key={`feature-${item.product_id}-${fi}`}
                            >
                              <span className="mt-0.5 inline-flex size-5 shrink-0 items-center justify-center rounded-full border border-primary/25 bg-primary/10 text-[#ff9f93]">
                                <Check className="size-3" />
                              </span>
                              <span>{feature}</span>
                            </li>
                          ))}
                        </ul>
                      ) : null}

                      <div className="mt-auto flex flex-col gap-2 pt-8">
                        {/* Manual Payment Option for Chinese Users */}
                        {showManualPay && !checkoutPaused && (
                          <Button
                            variant="outline"
                            className="h-12 w-full rounded-full border-green-600/70 text-green-600 hover:bg-green-50 hover:text-green-700"
                            disabled={isItemLoading}
                            onClick={() => {
                              if (isLoading) return;
                              handleManualPay(item);
                            }}
                          >
                            {isItemLoading ? (
                              <>
                                <Loader className="mr-2 h-4 w-4 animate-spin" />
                                处理中...
                              </>
                            ) : (
                              <>
                                <Image
                                  src="https://pub-e1eb76428e24457ebfc067c635cb4fc4.r2.dev/imgs/cnpay.png"
                                  alt="CNY"
                                  width={20}
                                  height={20}
                                  className="w-5 h-5"
                                />
                                支付宝/微信 ¥{(item.cn_amount || 0) / 100}
                              </>
                            )}
                          </Button>
                        )}

                        {checkoutPaused ? (
                          <p className="text-center text-sm text-muted-foreground">
                            New orders are not being accepted at this time.
                          </p>
                        ) : item.button ? (
                          <Button
                            className={cn(
                              "h-12 w-full rounded-full font-semibold",
                              isVisualFeatured
                                ? "landing-hero-button bg-primary text-primary-foreground"
                                : "landing-hero-button border border-white/16 bg-white/6 text-foreground hover:bg-white/10"
                            )}
                            disabled={isItemLoading}
                            onClick={() => {
                              if (isLoading) return;
                              handleCheckout(item);
                            }}
                          >
                            {(!isLoading || !isItemLoading) && <p>{buttonTitle}</p>}
                            {isItemLoading && <p>{buttonTitle}</p>}
                            {isItemLoading && <Loader className="mr-2 h-4 w-4 animate-spin" />}
                            {item.button.icon && (
                              <Icon name={item.button.icon} className="size-4" />
                            )}
                          </Button>
                        ) : null}
                        {item.tip ? (
                          <p className="mt-2 text-center text-sm text-muted-foreground">
                            {item.tip}
                          </p>
                        ) : null}
                      </div>
                    </div>
                  </article>
                );
              })}
          </div>
        </div>
      </section>

      {/* Manual Payment Modal */}
      <ManualPaymentModal
        isOpen={isManualPayModalOpen}
        onClose={() => setIsManualPayModalOpen(false)}
        item={manualPayItem}
      />
    </>
  );
}
